import { useEffect } from 'react';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';
import { ApiError } from '../api';
import { t } from '../i18n';
import { reportClientError } from './reportClientError';
import { Button } from './ui/Button';
import { cardClass } from './ui/Card';

/**
 * What one broken screen looks like.
 *
 * There is a root ErrorBoundary, and until now it was the only one — so a
 * null-deref while rendering the payroll screen took the header, the tabs and
 * the bottom nav with it, and the person was left on a white page with no way
 * to anywhere else. Mounted as a route errorElement *inside* each layout, so
 * what breaks is the panel between the navigation, and the next tab is still
 * one tap away.
 *
 * React Router catches these itself, which means the root ErrorBoundary never
 * sees them — hence the report from here.
 */
export function RouteError() {
  const error = useRouteError();
  const nav = useNavigate();

  useEffect(() => {
    console.error('[RouteError]', error);
    reportClientError('error-boundary', error);
  }, [error]);

  return (
    <div className={cardClass('danger', 'mx-auto w-full max-w-sm space-y-3 text-center')}>
      <p className="font-heading text-lg font-bold">{t('Something went wrong')}</p>
      <p className="text-sm text-urgent/80">{message(error)}</p>
      <div className="flex flex-col gap-2">
        {/* go(0) and not assign('/'): the address is the one thing worth
            keeping — a reload lands back on the screen they asked for. */}
        <Button variant="danger" className="w-full" onClick={() => nav(0)}>
          {t('Try again')}
        </Button>
        <Button variant="ghost" className="w-full" onClick={() => nav(-1)}>
          {t('Back')}
        </Button>
      </div>
    </div>
  );
}

function message(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (isRouteErrorResponse(error)) return error.statusText || t('Loading failed');
  return t('This screen hit a problem. The rest of the app is fine.');
}
